/**
 * Where the browser lands after Microsoft: /auth/microsoft.
 *
 * The server redirects here with its answer in the URL FRAGMENT, never the
 * query, so it is not sent to any server or written to an access log:
 *
 *   #code=<one-time handoff code>                    login succeeded at Microsoft
 *   #error=<CODE>                                    login refused
 *   #stepup=ok&returnTo=<urlencoded path+query>      step-up confirmed
 *   #error=<CODE>&flow=stepup&returnTo=<urlencoded>  step-up refused
 *
 * The first thing this page does is read the fragment and remove it from the
 * address bar and history (replaceState), because a handoff code left there is
 * a credential in the browser's history. A code is then traded, once, for the
 * same session body a password login returns, and the session is completed by
 * the same code the password login uses (UserContext.completeSsoLogin).
 *
 * Every destination that arrives in the URL passes `safeReturnString`; nothing
 * here follows a bare `startsWith('/')`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useUser } from '../contexts/UserContext';
import { defaultDestination } from '../components/Auth/postSignIn';
import { exchangeSsoCode, safeReturnString, ssoErrorCopy } from '../services/ssoApi';

type View =
  | { kind: 'working' }
  | { kind: 'error'; code: string | null; stepUp: boolean; returnTo: string | null };

export default function AuthMicrosoft() {
  const navigate = useNavigate();
  const { completeSsoLogin, user } = useUser();
  const [view, setView] = useState<View>({ kind: 'working' });
  // The fragment is consumed exactly once. React may run this effect twice in development; the
  // second run would find an empty fragment and misreport a good sign-in as a failure.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    // Before anything else: the fragment (and any code in it) leaves the address bar and history.
    try {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    } catch { /* history unavailable: nothing more can be done */ }

    const code = params.get('code');
    const errorCode = params.get('error');
    const stepUp = params.get('flow') === 'stepup' || params.get('stepup') !== null;
    const returnTo = safeReturnString(params.get('returnTo'));

    const fail = (c: string | null) => setView({ kind: 'error', code: c, stepUp: params.get('flow') === 'stepup', returnTo });

    (async () => {
      if (errorCode) { fail(errorCode); return; }

      if (params.get('stepup') === 'ok') {
        toast.success('Confirmed with Microsoft. Do the action again to finish it.');
        navigate(returnTo || (await defaultDestination(user?.role)), { replace: true });
        return;
      }

      if (code && !stepUp) {
        const result = await exchangeSsoCode(code);
        if (!result.ok) { fail(result.code); return; }
        const outcome = await completeSsoLogin(result.data);
        if (outcome.kind !== 'success') { fail(null); return; }
        const destination = safeReturnString(result.data?.returnTo)
          || await defaultDestination(result.data?.user?.role);
        navigate(destination, { replace: true });
        return;
      }

      fail(null);
    })();
    // Runs once per landing; the guard above makes the dependency list irrelevant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (view.kind === 'working') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6faf9] px-4 text-[#10252d]">
        <div role="status" className="flex items-center gap-3 text-sm">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Finishing your Microsoft sign-in…
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6faf9] px-4 py-10 text-[#10252d]">
      <div className="w-full max-w-md rounded-lg border border-[#d7e5e1] bg-white p-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#fbeeee]">
          <AlertTriangle className="h-6 w-6 text-[#b94343]" aria-hidden="true" />
        </div>
        <h1 className="mt-4 text-xl font-semibold">Microsoft sign-in</h1>
        <p role="alert" className="mt-2 text-sm leading-6 text-[#60747b]">{ssoErrorCopy(view.code)}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/login"
            className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white"
          >
            Back to sign in
          </Link>
          {view.stepUp && view.returnTo && (
            <Link
              to={view.returnTo}
              className="inline-flex h-11 items-center justify-center rounded-md border border-[#d7e5e1] px-5 text-sm font-semibold"
            >
              Go back
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
